import { useEffect } from 'react';

import { useEffectiveSettings, useOverrideSetting } from '@/features/admin/api';
import { appearanceFromSettings, applyAppearance, readCachedAppearance } from '@/lib/personalization';
import { useUiStore, type ThemeMode } from '@/lib/ui-store';

/**
 * BR-ADM-08-03 — "Every setting must survive signing out and must FOLLOW THE
 * PERSON TO ANOTHER COMPUTER."
 *
 * Mounted once, in the shell. `/api/settings/effective` is the person's own
 * server-stored row set; the moment it answers, its values replace whatever
 * the local cache painted on the first frame. §15's "two users at once" row
 * describes exactly this: "one person on a phone and a laptop. The later
 * change wins, and the other device picks it up on its next page load."
 */
export function useAppearanceSync(): void {
  const { data: settings } = useEffectiveSettings();
  const setTheme = useUiStore((s) => s.setTheme);

  useEffect(() => {
    if (!settings) return;
    const appearance = appearanceFromSettings(settings);
    applyAppearance(appearance);
    // The top bar's button reads the store, so the store has to learn what the
    // server said or the next click would toggle away from a stale value.
    setTheme(appearance['appearance.theme'] === 'dark' ? 'dark' : 'light');
  }, [settings, setTheme]);
}

/**
 * The top bar's theme button, made to STICK.
 *
 * GEN-14 gave the product a theme toggle years before ADM-08 gave it a home,
 * and it wrote to `localStorage` alone — so a person who chose Dark on their
 * laptop still met a white screen on the shop computer, which is precisely the
 * defect BR-ADM-08-03 names ("the prototype keeps them in the open browser tab
 * only"). The button now writes the same `appearance.theme` row the
 * Personalization screen writes, so the two controls cannot disagree.
 *
 * It goes through the settings API's `@SelfService` override path — no
 * permission, never audited (BR-ADM-08-12: "changes here must NOT be written
 * to the Audit Log. They are not business events"). Applied first, recorded
 * after: a slow network delays the record, never the effect (BR-ADM-08-01).
 */
export function usePersistentTheme(): { theme: ThemeMode; toggle: () => void } {
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const override = useOverrideSetting();

  return {
    theme,
    toggle: () => {
      const next: ThemeMode = theme === 'dark' ? 'light' : 'dark';
      setTheme(next);
      applyAppearance({ ...readCachedAppearance(), 'appearance.theme': next });
      // ERR-ADM-08-02's shape: a failed write leaves the choice applied on this
      // device. The screen that owns this setting says so out loud; the top bar
      // has no room for a sentence, and silently reverting the button a person
      // just pressed would be the worse behaviour.
      override.mutate({ key: 'appearance.theme', value: next });
    },
  };
}
